import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { color, r } from '../../theme/tokens';
import {
  ANALYZE_HINT_NEEDS_BOTH,
  ANALYZE_HINT_NEEDS_GOAL,
  STACK_NAME_LABEL,
  STACK_NAME_PLACEHOLDER,
  STACK_NAME_REQUIRED,
  SUPPLEMENTS_REQUIRED,
} from './copy';

/**
 * The visible "required" mark beside a field's caption. MUI draws its own
 * asterisk only on a TextField's `label`, and these fields use a separate
 * caption, so `required` alone showed nothing (2026-10-04). Hidden from
 * screen readers: the input's own `required` already announces it.
 */
export const RequiredMark = () => (
  <Box component="span" aria-hidden="true" sx={{ color: 'error.main', marginLeft: r(3) }}>*</Box>
);

/** Props that turn a name TextField's "missing" state on: red underline, the reason under it. */
export const nameFieldErrorProps = (missing) => (missing
  ? { error: true, helperText: STACK_NAME_REQUIRED }
  : {});

/**
 * The Analyze page's own header, field and hint, per Tanu's Analyze Stack
 * engineering handoff (2026-09-24). Kept out of `StackForm`, which also draws
 * the plain edit page — those keep the look they had, because the handoff is
 * about the analysis surface only.
 */

/** Section headings: "Health goals", "Your supplements". */
export const SECTION_HEADING_SX = {
  fontWeight: 800,
  fontSize: r(20),
  letterSpacing: '-0.02em',
  color: color.ink,
  mb: r(12),
};

export const AnalyzeTitle = ({ children }) => (
  <Typography
    component="h1"
    sx={{
      fontWeight: 800,
      fontSize: r(30),
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      color: color.evergreen,
      textAlign: 'center',
      mb: r(24),
    }}
  >
    {children}
  </Typography>
);

const STACK_NAME_ID = 'analyze-stack-name';

/**
 * A labelled field rather than a caption beside it: the label sits above, so
 * the input has a real accessible name without a separate `aria-label`.
 */
export const StackNameField = ({ value, onChange, missing = false, inputRef }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: r(6), maxWidth: r(440), mb: r(24) }}>
    {/* The mark sits beside the label, not inside it, so the field's
        accessible name stays exactly "Stack name". */}
    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
      <Typography
        component="label"
        htmlFor={STACK_NAME_ID}
        sx={{
          fontWeight: 700,
          fontSize: r(11),
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: color.inkSoft,
        }}
      >
        {STACK_NAME_LABEL}
      </Typography>
      <RequiredMark />
    </Box>
    <TextField
      id={STACK_NAME_ID}
      value={value}
      onChange={onChange}
      inputRef={inputRef}
      {...nameFieldErrorProps(missing)}
      placeholder={STACK_NAME_PLACEHOLDER}
      variant="standard"
      fullWidth
      required
      sx={{
        '& .MuiInput-root': {
          fontWeight: 700,
          fontSize: r(18),
          letterSpacing: '-0.01em',
          color: color.ink,
          '&:before': { borderBottom: `${r(1)} solid ${color.line}` },
          '&:hover:not(.Mui-disabled):before': { borderBottom: `${r(1)} solid ${color.line}` },
          '&.Mui-focused:after': { borderBottom: `${r(1)} solid ${color.accent}` },
        },
        '& .MuiInput-input': { padding: `${r(6)} 0 ${r(8)}` },
      }}
    />
  </Box>
);

/**
 * What a disabled Analyze is waiting for, or `null` when nothing is missing.
 * Pure so the three cases can be asserted without rendering the form.
 */
export const analyzeHintText = ({ hasGoal, hasSupplements }) => {
  if (!hasGoal && !hasSupplements) return ANALYZE_HINT_NEEDS_BOTH;
  if (!hasGoal) return ANALYZE_HINT_NEEDS_GOAL;
  if (!hasSupplements) return SUPPLEMENTS_REQUIRED;
  return null;
};

/** The hint itself: muted, right-aligned, beside the footer buttons. */
export const AnalyzeHint = ({ children }) => (
  <Typography
    data-testid="analyze-hint"
    sx={{
      flex: 1,
      minWidth: r(200),
      fontSize: r(13),
      color: color.inkSoft,
      textAlign: { xs: 'left', sm: 'right' },
    }}
  >
    {children}
  </Typography>
);
